
import { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { OnChange } from 'react-final-form-listeners'
import { Form } from 'react-final-form'
import { TextField, Checkboxes, Select as RffSelect } from 'mui-rff'
import { Card, CardContent, makeStyles, Grid, Select, MenuItem, InputAdornment, ListItemIcon, Divider, IconButton, Tooltip, Collapse, Fade, Table, TableRow, TableCell, TableBody } from '@material-ui/core'
import { ShortText, RadioButtonChecked, CheckBox, ArrowDropDownCircle, AssignmentTurnedIn, LinearScale, GridOn, CloudUpload, Check, InsertPhoto, Subject, Link, Web, Delete, FileCopy, CallSplit, DragIndicator, CalendarViewDay, DynamicFeed } from '@material-ui/icons'
import { validationRuleFormValues, validationRuleRangeValues } from 'utils/rules'
import TableComponent from './taskTableComponent'
import ImageComponent from './taskImageComponent'
import TextBox from 'components/textbox/textbox'
import { deleteArrayIndex } from 'utils/utils'
import CardTitle from 'components/card/title'
import { isUserInputType } from 'utils/rules'
import TaskConditional from './taskConditional'
import VariableAutofillTextField from 'components/input/variableinput'
import { v4 as uuid } from 'uuid'
import QuillOutput from 'components/textbox/quilloutput'

const useStyles = makeStyles(() => ({
  spacing: {
    marginTop: '10px'
  },
  table: {
    tableLayout: 'fixed',
    '& td': {
      border: '1px solid #eee',
      textAlign: 'center',
      padding: '5px'
    },
    '& tr:hover': {
      backgroundColor: '#fcfcfc'
    }
  },
  tableHeader: {
    fontWeight: '800',
    textAlign: 'center'
  },
  typeChooser: {
    width: '270px',
    display: 'flex',
    '& svg': {
      height: '19px'
    },
    '& .MuiListItemIcon-root': {
      minWidth: '30px'
    }
  },
  cardClick: {
    '& .Mui-disabled': {
      cursor: 'pointer',
      color: 'rgba(0, 0, 0, 0.87)'
    },
    '& .MuiInput-underline.Mui-disabled:before': {
      display: 'none'
    },
    '& .MuiSelect-icon.Mui-disabled': {
      display: 'none'
    },
  },
  dragIndicator: {
    position: 'absolute',
    top: '-22px',
    left: '50%',
    transform: 'rotate(90deg)'
  },
  hoverIcons: {
    '& .icons': {
      display: 'none',
    },
    '&:hover .icons': {
      display: 'block',
    }
  },
  conditionalBox: {
    backgroundColor: 'rgb(246, 246, 246)',
  },
  idnumber: {
    position: 'relative',
    '& span': {
      position: 'absolute',
      right: '0px',
      color: 'rgb(183, 183, 183)'
    }
  }
}))
export default function TaskComponent({ task, filepath, component, componentIndex, save, saveFast, selected, setSelected, limitOptions, dragHandleProps, noHoverIcons }) {
  const classes = useStyles()
  const [conditionals, setConditionals] = useState(false)
  const cloneComponent = () => {
    const Components = JSON.parse(JSON.stringify(task.Components))
    const newComponent = { ...component, ComponentID: uuid().split('-')[0] }
    Components.splice(componentIndex, 0, newComponent)
    saveFast({ Components })
  }

  useEffect(() => {
    setConditionals(component?.ConditionalRules?.length > 0 ?? false)
  }, [component.ConditionalRules])

  const deleteComponent = e => {
    e.stopPropagation()
    saveFast({ Components: deleteArrayIndex(task.Components, componentIndex) })
  }
  const saveComponent = (newVal) => {
    if (selected != componentIndex) return
    if (!task.Components) return
    const Components = JSON.parse(JSON.stringify(task.Components))
    Components[componentIndex] = newVal
    save({ Components })
  }
  const saveComponentFast = (newVal) => {
    if (selected != componentIndex) return
    if (!task.Components) return
    const Components = JSON.parse(JSON.stringify(task.Components))
    Components[componentIndex] = newVal
    saveFast({ Components })
  }

  const iconButtons = <div className='icons' ><CardTitle right={<>
    {!limitOptions && <Tooltip title={`${conditionals ? 'Hide' : 'Show'} Conditional Rules`}><IconButton size='small' color='primary' onClick={() => setConditionals(!conditionals)}><CallSplit style={{ transform: 'rotate(180deg)' }} /></IconButton></Tooltip>}
    {!limitOptions && <Tooltip title='Clone'><IconButton size='small' color='primary' onClick={cloneComponent}><FileCopy /></IconButton></Tooltip>}
    <Tooltip title='Delete'><IconButton size='small' color='secondary' onClick={deleteComponent}><Delete /></IconButton></Tooltip>
  </>} marginBottom={1} additional={!limitOptions ? <Tooltip title='Drag to Reorder' className={classes.dragIndicator}><div {...dragHandleProps}><DragIndicator /></div></Tooltip> : null} /></div>

  return <Card onClick={() => setSelected(componentIndex)} style={{ cursor: selected != componentIndex ? 'pointer' : 'initial' }}>
    <CardContent className={selected === componentIndex ? '' : `${classes.hoverIcons} ${classes.cardClick}`}>
      {!noHoverIcons && iconButtons}
      {selected === componentIndex ?
        <Grid container>
          <Grid item xs={12}>
            <ComponentTypeChooser task={task} component={component} componentIndex={componentIndex} save={saveFast} limitOptions={limitOptions} />
            <TaskComponentEditor task={task} disable={false} component={component} filepath={filepath} save={saveComponent} saveFast={saveComponentFast} />
            {isUserInputType(component) && <AllTaskOptions component={component} save={saveComponent} task={task} />}
          </Grid>
          <Fade in={conditionals} style={{ display: conditionals ? '' : 'none' }}>
            <Grid item xs={12}>
              <TaskConditional components={task?.Components ?? []} component={component} saveFast={saveFast} save={save} curIndex={componentIndex} />
            </Grid>
          </Fade>
        </Grid> :
        <>
          <ComponentTypeChooser disable={true} task={task} component={component} componentIndex={componentIndex} save={save} />
          <TaskComponentEditor disable={true} component={component} filepath={filepath} save={saveComponent} saveFast={saveComponentFast} />
          {component?.ConditionalRules?.length > 0 && <div className={classes.conditionalBox}>
            <TaskConditional components={task?.Components ?? []} component={component} saveFast={saveFast} save={save} curIndex={componentIndex} disabled={true} />
          </div>}
        </>
      }
      <div className={classes.idnumber}><span># {component.ComponentID}</span></div>
    </CardContent>
  </Card >
}

TaskComponent.propTypes = {
  task: PropTypes.object.isRequired,
  filepath: PropTypes.string.isRequired,
  component: PropTypes.object.isRequired,
  componentIndex: PropTypes.number.isRequired,
  save: PropTypes.func.isRequired,
  saveFast: PropTypes.func.isRequired,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
  setSelected: PropTypes.func.isRequired,
  limitOptions: PropTypes.bool,
  dragHandleProps: PropTypes.object,
  noHoverIcons: PropTypes.bool,
}

function ComponentTypeChooser({ task, component, componentIndex, save, disable, limitOptions }) {

  // If radio or checkboxes, need to do some parsing
  if (component.Type === 'radioButtons' && component.Multiple) component.Type = 'checkboxes'

  const classes = useStyles()
  const onChange = (event) => {
    if (disable) return
    const Components = JSON.parse(JSON.stringify(task.Components))
    Components[componentIndex].Type = event.target.value

    // If radio or checkboxes, need to do some parsing
    if (Components[componentIndex].Type === 'radioButtons') {
      Components[componentIndex].Multiple = false
    } else if (Components[componentIndex].Type === 'checkboxes') {
      Components[componentIndex].Type = 'radioButtons'
      Components[componentIndex].Multiple = true
    }
    Components[componentIndex].Value = null
    save({ Components })
  }
  let options = [
    <MenuItem key={'image'} value={'image'}><ListItemIcon><InsertPhoto /></ListItemIcon><span>Image / PDF / Audio / Video / File</span></MenuItem>,
    <MenuItem key={'textBlock'} value={'textBlock'}><ListItemIcon><Subject /></ListItemIcon><span>Text Block / Paragraph</span></MenuItem>,
    <MenuItem key={'link'} value={'link'}><ListItemIcon><Link /></ListItemIcon><span>Link</span></MenuItem>,
    <MenuItem key={'iFrame'} value={'iFrame'}><ListItemIcon><Web /></ListItemIcon><span>iFrame</span></MenuItem>,
    <MenuItem key={'lineBreak'} value={'lineBreak'}><ListItemIcon><CalendarViewDay /></ListItemIcon><span>Section Break</span></MenuItem>,
  ]
  if (!limitOptions) {
    options = [
      <MenuItem key={'textInput'} value={'textInput'}><ListItemIcon><ShortText /></ListItemIcon><span>Text Input</span></MenuItem>,
      <MenuItem key={'radioButtons'} value={'radioButtons'}><ListItemIcon><RadioButtonChecked /></ListItemIcon><span>Multiple Choice</span></MenuItem>,
      <MenuItem key={'checkboxes'} value={'checkboxes'}><ListItemIcon><CheckBox /></ListItemIcon><span>Checkboxes</span></MenuItem>,
      <MenuItem key={'dropdown'} value={'dropdown'}><ListItemIcon><ArrowDropDownCircle /></ListItemIcon><span>Drop Down</span></MenuItem>,
      <MenuItem key={'checklist'} value={'checklist'}><ListItemIcon><AssignmentTurnedIn /></ListItemIcon><span>Checklist</span></MenuItem>,
      <MenuItem key={'range'} value={'range'}><ListItemIcon><LinearScale /></ListItemIcon><span>Range</span></MenuItem>,
      <MenuItem key={'tableInput'} value={'tableInput'}><ListItemIcon><GridOn /></ListItemIcon><span>Table</span></MenuItem>,
      <MenuItem key={'fileUpload'} value={'fileUpload'}><ListItemIcon><CloudUpload /></ListItemIcon><span>File Upload</span></MenuItem>,
      <MenuItem key={'confirmation'} value={'confirmation'}><ListItemIcon><Check /></ListItemIcon><span>Confirmation</span></MenuItem>,
      <MenuItem key={'templatePDF'} value={'templatePDF'}><ListItemIcon><DynamicFeed /></ListItemIcon><span>PDF Template</span></MenuItem>,
      <Divider key={'div'} />,
      ...options
    ]
  }
  return <Select
    value={component.Type}
    onChange={onChange}
    variant={disable ? 'standard' : 'outlined'}
    inputProps={{ className: classes.typeChooser }}
    disabled={disable}
    startAdornment={< InputAdornment position="end" > {componentIndex + 1})&nbsp;</InputAdornment >}
  >
    {options}
  </Select >
}
ComponentTypeChooser.propTypes = {
  task: PropTypes.object.isRequired,
  component: PropTypes.object.isRequired,
  componentIndex: PropTypes.number.isRequired,
  save: PropTypes.func.isRequired,
  disable: PropTypes.bool,
  limitOptions: PropTypes.bool
}

function AllTaskOptions({ component, task, save }) {
  return <Form
    onSubmit={() => { }}
    initialValues={component}
    render={({ values }) => (<>
      <Checkboxes name='AllowBlanks' variant='outlined' data={{ label: 'This question is optional', value: true }} />
      <OnChange name='AllowBlanks'>{() => { save(values) }}</OnChange>
      <Checkboxes name='ShowHelpTooltip' variant='outlined' data={{ label: <Tooltip title='Show a little tooltip like this one to provide more context about this question'><span>Help tooltip</span></Tooltip>, value: true }} />
      <OnChange name='ShowHelpTooltip'>{() => { save(values) }}</OnChange>
      <div style={{ display: values.ShowHelpTooltip ? '' : 'none' }}>
        <VariableAutofillTextField variant='outlined' name='HelpTooltip' label='Help Tooltip Text'
          save={save} values={values} task={task} />
      </div>
    </>)} />
}
AllTaskOptions.propTypes = {
  component: PropTypes.object.isRequired,
  task: PropTypes.object.isRequired,
  save: PropTypes.func.isRequired,
}



function TaskComponentEditor({ task, component, filepath, save, saveFast, disable }) {
  const classes = useStyles()
  let out = <></>
  if (component.Type === 'textInput') {
    if (!component.ValidationRule) component.ValidationRule = 'Any'
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. First Name)' }, { name: 'Example', label: 'Example (ex. John Doe)' }]}
      additionalFields={(values) => <>
        <Collapse in={!values.RichText}>
          <Tooltip title='Choose what kind of data can be entered' placement='top-start'><span>
            <RffSelect label='Formating / Validation' name='ValidationRule' variant='outlined' data={validationRuleFormValues()} />
          </span></Tooltip>
          <OnChange name='ValidationRule'>{() => { save(values) }}</OnChange>
        </Collapse>
        <Tooltip title='Allow full rich text like paragraphs, lists, etc. Note: Using the value of this question in a variable will display the full HTML formatting' placement='top-start'><span>
          <Checkboxes name='RichText' variant='outlined' data={{ label: 'Multi-Line Rich Text', value: true }} />
          <OnChange name='RichText'>{() => { save(values) }}</OnChange>
        </span></Tooltip>
      </>} />
  } else if (component.Type === 'radioButtons' || component.Type === 'checkboxes' || component.Type === 'dropdown') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Country)' }, { name: 'Options', label: 'Options (seperated by commas)' }]}
      additionalFields={(values) => <>
        {component.Type === 'dropdown' ? <>
          <Checkboxes name='Autocomplete' variant='outlined' data={{ label: 'Type to Search', value: true }} />
          <OnChange name='Autocomplete'>{() => { save(values) }}</OnChange>
          {values.Autocomplete &&
            <>
              <Checkboxes name='Freeform' variant='outlined' data={{ label: 'Free Form Input', value: true }} />
              <OnChange name='Freeform'>{() => { save(values) }}</OnChange>
            </>
          }
        </> :
          <>
            <Checkboxes name='Freeform' variant='outlined' data={{ label: 'Allow "Other"', value: true }} />
            <OnChange name='Freeform'>{() => { save(values) }}</OnChange>
          </>}
      </>}
    />
  } else if (component.Type === 'range') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Date Range)' }, { name: 'Range1', label: 'Starting Range Prompt' }, { name: 'Range2', label: 'Ending Range Prompt' }]}
      additionalFields={(values) => <>
        <Tooltip title='Choose what kind of data can be entered' placement='top-start'><span>
          <RffSelect label='Formating / Validation' name='ValidationRule' variant='outlined' data={validationRuleRangeValues()} />
        </span></Tooltip>
        <OnChange name='ValidationRule'>{() => { save(values) }}</OnChange>
      </>} />
  } else if (component.Type === 'fileUpload') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Logo Icon)' }]} additionalFields={(values) => <>
      <Checkboxes name='ClientReadOnly' variant='outlined' data={{ label: 'Don\'t allow client uploads', value: true }} />
      <OnChange name='ClientReadOnly'>{() => { save(values) }}</OnChange>
    </>} />
  } else if (component.Type === 'confirmation') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Did you enable the API?)' }, { name: 'ConfirmationText', label: 'Confirmation Text (Default: Yes)' }]} />
  } else if (component.Type === 'link') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Click here to view details)' }, { name: 'URL', label: 'URL (ex. https://www.google.com)' }]} />
  } else if (component.Type === 'templatePDF') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'PDF Name' }]} additionalFields={(values) => <>
      <Tooltip title='Choose Template' placement='top-start'><span>
        <RffSelect label='Template' name='Template' variant='outlined' data={[{ label: 'Voided Check', value: 'voidCheck' }, { label: 'W9', value: 'W9' }]} />
      </span></Tooltip>
      <OnChange name='Template'>{() => { save(values) }}</OnChange>
      {values.Template === 'voidCheck' && <>
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Routing' label='Routing Number' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Account' label='Account Number' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Name' label='Name' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Address1' label='Address Line One' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Address2' label='Address Line Two' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Phone' label='Phone Number' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.BankName' label='Bank Name' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Memo' label='Memo' variant='outlined' save={save} values={values} task={task} />
      </>}
      {values.Template === 'W9' && <>
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Name' label='Name' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.BusinessName' label='Business Name' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.SSN' label='SSN' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.EIN' label='EIN' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.Address' label='Address (number, street, and apt. or suite no.)' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.City' label='City' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.State' label='State' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.ZIP' label='ZIP' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.BusinessType' label='Business Type - (Sole Proprietor, C Corporation, S Corporation, Partnership)' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.RequesterName' label='Requester Name' variant='outlined' save={save} values={values} task={task} />
        <VariableAutofillTextField className={classes.spacing} name='PDFVars.RequesterAddress' label='Requester Full Address' variant='outlined' save={save} values={values} task={task} />
      </>}
    </>} />
  } else if (component.Type === 'lineBreak') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Title' }]}
      additionalFields={(values) => <>
        <Tooltip title='Choose what kind of data can be entered' placement='top-start'><span>
          <RffSelect label='Size' name='Size' variant='outlined' data={[{ label: 'Small', value: 'Small' }, { label: 'Medium', value: 'Medium' }, { label: 'Large', value: 'Large' },]} />
        </span></Tooltip>
        <OnChange name='Size'>{() => { save(values) }}</OnChange>
        <Checkboxes name='Line' variant='outlined' data={{ label: 'Horizontal Line Break', value: true }} />
        <OnChange name='Line'>{() => { save(values) }}</OnChange>
      </>} />
  } else if (component.Type === 'iFrame') {
    const preSaveHook = val => {
      if (val.startsWith('<iframe')) {
        const div = document.createElement('div')
        div.innerHTML = val
        return div.querySelector('iframe').getAttribute('src')
      }
      return val
    }
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable}
      fields={[{ name: 'Prompt', label: 'Title' }, { name: 'URL', label: 'URL / Embed Code (leave blank if unique per on-boarding)', preSaveHook }, { name: 'Height', label: 'iFrame Height in Pixels (default: 1000)', type: 'number' }, { name: 'ExitMessage', label: 'Popup text when mouse leaves iFrame (leave blank for none)' },]}
      additionalFields={(values) => <>
        <Checkboxes name='HideURL' variant='outlined' data={{ label: 'Hide URL', value: true }} />
        <OnChange name='HideURL'>{() => { save(values) }}</OnChange>
        <Tooltip title='The proxy can be useful to show content blocked from being iFramed. It will not work for content that requires a log in.' placement='top-start'><span>
          <Checkboxes name='Proxy' variant='outlined' data={{ label: 'Use Proxy', value: true }} />
        </span></Tooltip>
        <OnChange name='Proxy'>{() => { save(values) }}</OnChange>
        {values.Proxy && <Tooltip title='The advanced proxy is not as flexible and is slower, but can work with all webpages, including pages that require a login.' placement='top-start'><span>
          <Checkboxes name='UIF' variant='outlined' data={{ label: 'Use Advanced Proxy', value: true }} />
        </span></Tooltip>}
        <OnChange name='UIF'>{() => { save(values) }}</OnChange>
      </>} />
  } else if (component.Type === 'checklist') {
    out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Tasks to Complete)' }, { name: 'Rows', label: 'Number of items in Checklist', hide: true, type: 'number', fast: true }]}
      additionalFields={(values) => <Table className={classes.table}><TableBody>
        <TableRow><TableCell className={classes.tableHeader}>Checklist Items</TableCell></TableRow>
        {
          [...Array(Math.max(parseInt(values.Rows), 1) || 3).keys()].map(i => <TableRow key={i}>
            <TableCell>
              <TextField name={`Options.${i}`} variant='outlined' />
              <OnChange name={`Options.${i}`}>{() => { save(values) }}</OnChange>
            </TableCell>
          </TableRow>)
        }
      </TableBody></Table>
      } />
  } else if (component.Type === 'tableInput') {
    out = <>
      <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Prompt (ex. Rate Table)' }, { name: 'Columns', label: 'Columns', hide: true, type: 'number', fast: true }, { name: 'Rows', label: 'Rows', hide: true, type: 'number', fast: true }]} />
      {!disable && <TableComponent component={component} save={save} saveFast={saveFast} />}
    </>
  } else if (component.Type === 'image') {
    out = <ImageComponent component={component} save={saveFast} filepath={filepath} disable={disable} />
  } else if (component.Type === 'textBlock') {
    if (disable) {
      out = <QuillOutput html={component.HTML || component.Prompt} center={component.CenterText} />
    } else {
      const deltas = component.QuillDeltas ? JSON.parse(component.QuillDeltas) : []
      out = <CreateComponent component={component} save={save} saveFast={saveFast} task={task} disable={disable} fields={[{ name: 'Prompt', label: 'Title' }]}
        additionalFields={(values) => <>
          <TextBox center={component.CenterText} task={task} deltas={deltas} save={(html, ops) => {
            values.HTML = html
            values.QuillDeltas = JSON.stringify(ops)
            save(values)
          }} />
          <Checkboxes name='CenterText' variant='outlined' data={{ label: <Tooltip title='Center align text in this paragraph'><span>Center Text</span></Tooltip>, value: true }} />
          <OnChange name='CenterText'>{() => { save(values) }}</OnChange>
          <Checkboxes name='FormatAsCode' variant='outlined' data={{ label: <Tooltip title='Render this paragraph as a codeblock with code formatting'><span>Format as Code</span></Tooltip>, value: true }} />
          <OnChange name='FormatAsCode'>{() => { save(values) }}</OnChange>
          <Checkboxes name='ShowHelpTooltip' variant='outlined' data={{ label: <Tooltip title='Show a little tooltip like this one to provide more context about this question'><span>Help tooltip</span></Tooltip>, value: true }} />
          <OnChange name='ShowHelpTooltip'>{() => { save(values) }}</OnChange>
          <div style={{ display: values.ShowHelpTooltip ? '' : 'none' }}>
            <VariableAutofillTextField variant='outlined' name='HelpTooltip' label='Help Tooltip Text'
              save={save} values={values} task={task} />
          </div>
        </>} />
    }
  }
  return out
}

TaskComponentEditor.propTypes = {
  task: PropTypes.object,
  component: PropTypes.object.isRequired,
  filepath: PropTypes.string,
  save: PropTypes.func,
  saveFast: PropTypes.func,
  disable: PropTypes.bool.isRequired,
}

function CreateComponent({ component, save, saveFast, task, disable, fields, additionalFields }) {
  const classes = useStyles()
  return <Form
    onSubmit={() => { }}
    subscription={{ values: true }}
    initialValues={component}
    render={({ values }) => (<>
      {fields.map((field, i) => <Fragment key={i}>
        {disable && field.hide ? <></> :
          <VariableAutofillTextField className={classes.spacing} variant={disable ? 'standard' : 'outlined'} name={field.name} label={disable ? '' : field.label} disabled={disable} type={field.type || 'text'}
            save={field.fast ? saveFast : save} values={values} task={task} preSaveHook={field.preSaveHook} />
        }
      </Fragment>)}
      <div className={classes.spacing}></div>
      {!disable && additionalFields ? additionalFields(values) : <></>}
    </>)}
  />
}

CreateComponent.propTypes = {
  component: PropTypes.object.isRequired,
  save: PropTypes.func.isRequired,
  fields: PropTypes.array.isRequired,
  disable: PropTypes.bool,
  additionalFields: PropTypes.func,
  saveFast: PropTypes.func,
  task: PropTypes.object,
}